'use client';

/**
 * VendorDetails - Vendor entity detail view
 *
 * @deprecated Use EntityDetailsLoader with DetailsPanel instead.
 * This component is kept for fallback during migration.
 * Remove after schema-driven rendering is verified.
 *
 * Displays vendor information when a vendor node is selected.
 * Fetches its own data via useVendorExplore hook.
 *
 * @component_role Vendor Detail View
 * @business_rule Shows vendor contact info, product count, collections,
 *   price range, and top products. Links to Odoo partner form.
 * @user_outcome Quick vendor assessment - see catalog size, pricing range,
 *   and popular products before engaging.
 * @integration_point klyde_edge /neo/graph/explore/vendor
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import {
  Factory,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useVendorExplore } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';

interface VendorDetailsProps {
  nodeId: string;
}

export function VendorDetails({ nodeId }: VendorDetailsProps) {
  const vendorExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_explore'));
  const { data, isLoading } = useVendorExplore(vendorExploreStrategy!, {
    vendor_id: nodeId,
    include_products: true,
    max_items: 10
  });
  const vendor = data?.vendor;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading vendor...
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="text-sm text-muted-foreground">
        Vendor details not available
      </div>
    );
  }

  const ODOO_HOST = process.env.NEXT_PUBLIC_ODOO_URL || 'http://klyde.vivita-ai.com';

  // Compute product stats
  const allProducts = [
    ...vendor.collections.flatMap(c => c.products),
    ...vendor.uncategorized_products
  ];
  const prices = allProducts.map(p => p.list_price).filter((p): p is number => p !== null);
  const minPrice = prices.length > 0 ? Math.min(...prices) : null;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : null;

  return (
    <div className="space-y-4">
      {/* Product Summary Stats */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 rounded bg-card border border-border text-center">
          <div className="text-lg font-semibold">{data?.metadata?.product_count}</div>
          <div className="text-xs text-muted-foreground">Products</div>
        </div>
        <div className="p-2 rounded bg-card border border-border text-center">
          <div className="text-lg font-semibold">{data?.metadata?.collection_count}</div>
          <div className="text-xs text-muted-foreground">Collections</div>
        </div>
        {minPrice !== null && maxPrice !== null && (
          <div className="p-2 rounded bg-card border border-border text-center col-span-2">
            <div className="text-sm font-semibold">
              ${minPrice.toFixed(0)} - ${maxPrice.toFixed(0)}
            </div>
            <div className="text-xs text-muted-foreground">Price Range</div>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground mb-2">CONTACT</h4>
        <div className="space-y-1 text-sm">
          {vendor.email && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Email:</span>
              <span className="truncate">{vendor.email}</span>
            </div>
          )}
          {vendor.phone && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Phone:</span>
              <span>{vendor.phone}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Type:</span>
            <span className="capitalize">{vendor.type}</span>
          </div>
        </div>
      </div>

      {/* Top Products */}
      {allProducts.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">TOP PRODUCTS</h4>
          <div className="grid grid-cols-2 gap-2">
            {allProducts.slice(0, 4).map((product) => (
              <div key={product.semantic_id} className="p-2 rounded bg-card border border-border">
                {product.image_url && (
                  <div className="aspect-square bg-muted rounded overflow-hidden mb-1">
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="text-xs font-medium truncate">{product.name}</div>
                {product.list_price && (
                  <div className="text-xs text-muted-foreground">${product.list_price.toFixed(0)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Collections */}
      {vendor.collections.length > 0 && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            COLLECTIONS ({vendor.collections.length})
          </h4>
          <div className="space-y-1">
            {vendor.collections.slice(0, 5).map((col) => (
              <div key={col.semantic_id} className="flex items-center justify-between p-2 rounded bg-card border border-border">
                <span className="text-sm truncate">{col.name}</span>
                <span className="text-xs text-muted-foreground shrink-0 ml-2">
                  {col.product_count} items
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open in Odoo */}
      {vendor.odoo_id && (
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">ACTIONS</h4>
          <a
            href={`${ODOO_HOST}/web#id=${vendor.odoo_id}&model=res.partner&view_type=form`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 w-full p-2 rounded-lg hover:bg-accent transition-colors text-sm"
          >
            <ExternalLink className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">{vendor.name || 'Vendor'}</span>
          </a>
        </div>
      )}
    </div>
  );
}
